import React from 'react';
import { Dialog } from '@mui/material';
import FormModalHeader from './FormModalHeader';
import FormModalFooter from './FormModalFooter';
import styles from './FormModal.module.scss';

export interface FormModalProps {
    isOpen: boolean;
    title: string;
    onClose: () => void;
    children: React.ReactNode;
    footer?: React.ReactNode;
    maxWidth?: number | string;
    showCloseButton?: boolean;
}

/**
 * Generic reusable modal shell for large/complex forms: a blue header (title +
 * close icon), a scrollable white body, and a right-aligned footer strip. MUI's
 * Dialog is used only for its portal/backdrop/focus-trap/Escape-to-close
 * mechanics — every visual detail comes from FormModal.module.scss, independent
 * of DialogModal or any other existing dialog's styling.
 *
 * Not tied to the Create New Handler form specifically — any future large form
 * (multi-row fields + Cancel/Save footer) can reuse this by supplying its own
 * field layout as `children` and its own buttons as `footer`.
 */
const FormModal: React.FC<FormModalProps> = ({
    isOpen,
    title,
    onClose,
    children,
    footer,
    maxWidth,
    showCloseButton = true
}) => (
    <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{ className: styles.paper, style: maxWidth ? { width: maxWidth } : undefined }}
    >
        <FormModalHeader title={title} onClose={onClose} showCloseButton={showCloseButton} />
        <div className={styles.body}>{children}</div>
        {footer && <FormModalFooter>{footer}</FormModalFooter>}
    </Dialog>
);

export default FormModal;
